/**
 * Shared CloudWatch RUM custom-events engine for @Hotels-at-Home/hah-asp-core — the ONLY place RUM
 * logic lives; every signal is a direct `recordRumEvent(...)` at the source. Contract:
 *  - Never touches `dataLayer`.
 *  - Safe no-op when unconfigured (`window.cwr` absent) and never throws — must not break the funnel.
 *  - No PII: never forward a raw response body; parse an allowlisted code via `rumErrorCode`.
 *  - Site-agnostic: defaults below work for every site running this package. A site overrides only
 *    the rare genuine divergence via `window.RUM_CONFIG` (shallow-merged over the defaults). The RUM
 *    *property* (AppMonitor / identity pool) is selected per-site/env in the host's `header.inc`
 *    `cwr` bootstrap (Web.config), so the engine never needs to know which site it is.
 *
 * Exposed on `window.RUM` / `window.recordRumEvent` for TS bundles and inline ASP includes.
 */

/** Event taxonomy — the only `type` values we emit. Use the constants so call sites can't drift. */
const RUM_EVENT = {
	CART_ERROR: 'CartError',
	PRODUCT_ERROR: 'ProductError',
	CHECKOUT_ERROR: 'CheckoutError',
	CHECKOUT_EVENT: 'CheckoutEvent',
};

/**
 * Default config — works for every site running this package (page names, the package-rendered
 * payment control ids, marker conventions are all shared). A site changes nothing unless it genuinely
 * diverges, in which case it sets `window.RUM_CONFIG` before this bundle loads and the values below
 * are shallow-merged over (one level deep for `paymentMethod`).
 */
const RUM_DEFAULTS = {
	// Reaching this page (server-gated by orderUI.ascx) is the reliable "order confirmed" signal.
	confirmationPathRegex: /sessionOrder\.aspx/i,
	// sessionStorage key for the in-flight-order marker (failed-order detection, set in submitOrder).
	inFlightMarkerKey: 'rumOrderInFlight',
	// Off the confirmation page, orders older than this are treated as stale, not failures.
	inFlightMaxAgeMs: 15 * 60 * 1000,
	// Payment-method resolution (best-effort at ORDER_SUBMITTED; vocabulary mirrors trackingjs.inc so
	// it's comparable to the authoritative ORDER_CONFIRMED value — never emit a label CONFIRMED can't).
	// The select/checkbox ids are rendered by the shared package control `newpaymentTypeUI.ascx`.
	paymentMethod: {
		select1Id: 'paymentTypeUI_ddlOrderCardType1',
		select2Id: 'paymentTypeUI_ddlOrderCardType2',
		secondaryChkId: 'paymentTypeUI_cUseSecondaryCard',
		giftCardValue: 'G',
	},
};

type RumConfig = typeof RUM_DEFAULTS;
const rumOverride = ((window as any).RUM_CONFIG || {}) as Partial<RumConfig> & { paymentMethod?: Partial<RumConfig['paymentMethod']> };

/** Effective config: defaults with the optional per-site `window.RUM_CONFIG` shallow-merged over. */
const RUM_CONFIG = {
	...RUM_DEFAULTS,
	...rumOverride,
	paymentMethod: { ...RUM_DEFAULTS.paymentMethod, ...(rumOverride.paymentMethod || {}) },
	// DEV-only: the server sets `window.RUM_DEBUG` on DEV (header.inc) and never on live, so debug
	// logging can't ship on. Not overridable via RUM_CONFIG — it's env-driven, not site-driven.
	debug: (window as any).RUM_DEBUG === true,
};

/**
 * DEBUG flag (DEV-only). While ON, events and raw responses are logged to the console for
 * inspection — console only, NEVER sent to RUM, so the No-PII gate on transmitted events is
 * unaffected. Toggle at runtime via `window.RUM.setDebug(...)`.
 */
let rumDebug = RUM_CONFIG.debug;

/** Centralized debug logger. No-op when `rumDebug` is off; never throws. */
function rumLog(...args: unknown[]): void {
	try {
		if (rumDebug && typeof console !== 'undefined' && console.log) {
			console.log('[RUM]', ...args);
		}
	} catch (e) {
		/* swallow — debug logging must never break the funnel */
	}
}

/**
 * Record a custom event to CloudWatch RUM. Safe no-op when `window.cwr` is absent; never throws.
 * @param type  Event taxonomy type (use a RUM_EVENT constant).
 * @param data  Allowlisted, PII-free fields.
 */
function recordRumEvent(type: string, data: Record<string, unknown> = {}): void {
	try {
		const cwr = (window as any).cwr;
		// cwr's command queue is defined synchronously in header.inc, so pre-load calls are buffered.
		if (typeof cwr === 'function') {
			cwr('recordEvent', { type: type, data: data });
			rumLog('recordEvent SENT →', type, data);
		} else {
			rumLog('recordEvent NO-OP (window.cwr absent) →', type, data);
		}
	} catch (e) {
		rumLog('recordEvent THREW (swallowed) →', type, data, e);
		/* swallow — telemetry must never break the funnel */
	}
}

/**
 * Extract an ALLOWLISTED `<code>` from a server response — never the raw body (bodies can carry
 * email/order data). Returns the code or 'UNKNOWN'. The PII-safe primitive for error events.
 */
function rumErrorCode(body: string): string {
	const m = /<code>([A-Z0-9_\-]{1,40})<\/code>/i.exec(body || '');
	const code = m ? m[1].toUpperCase() : 'UNKNOWN';
	// DEBUG ONLY (DEV): log the raw body to discover safe fields; only `code` is ever sent to RUM.
	rumLog('rumErrorCode parsed', code, '← raw body:', body);
	return code;
}

/**
 * Last-resort snippet for strings WE generate (known PII-free). Caps length; does NOT sanitize —
 * never pass a server response body here, use `rumErrorCode`.
 */
function rumSnippet(s: string): string {
	return (s || '').toString().replace(/\s+/g, ' ').trim().slice(0, 200);
}

/**
 * Best-effort card payment method for ORDER_SUBMITTED, read from RUM_CONFIG.paymentMethod (package DOM):
 * Credit Card / Gift Card / Credit Card + Gift Card. Used by the credit-card flow, where the label
 * genuinely varies by user selection; the Amazon and Points flows pass their own explicit label instead.
 * Authoritative value is the server-rendered one at ORDER_CONFIRMED; returns '' when undeterminable. Never throws.
 */
function rumPaymentMethod(): string {
	try {
		const cfg = RUM_CONFIG.paymentMethod;
		const sel1 = document.getElementById(cfg.select1Id) as HTMLSelectElement | null;
		if (!sel1) return '';
		const type1 = sel1.value;
		const chk = document.getElementById(cfg.secondaryChkId) as HTMLInputElement | null;
		const sel2 = document.getElementById(cfg.select2Id) as HTMLSelectElement | null;
		const type2 = (chk && chk.checked && sel2) ? sel2.value : '';
		if (type1 && type2 && type1 !== type2) return 'Credit Card + Gift Card';
		return type1 ? (type1 === cfg.giftCardValue ? 'Gift Card' : 'Credit Card') : '';
	} catch (e) {
		rumLog('paymentMethod resolve THREW (swallowed)', e);
		return '';
	}
}

/**
 * Set the in-flight-order marker (failed-order detection). Stores `{ts, paymentMethod}` as JSON so the
 * (deferred) ORDER_NOT_CONFIRMED load-check can attribute the failure — the payment DOM is gone by then.
 * paymentMethod is a coarse PII-free label. Never throws.
 */
function rumMarkOrderInFlight(paymentMethod?: string): void {
	try {
		sessionStorage.setItem(RUM_CONFIG.inFlightMarkerKey, JSON.stringify({ ts: Date.now(), paymentMethod: paymentMethod || '' }));
	} catch (e) {
		rumLog('markOrderInFlight THREW (swallowed)', e);
	}
}

/* Semantic helpers — one readable line per injection. `errorType`/`action` lead; */
/* callers add allowlisted fields (itemID, errorCode, step, paymentMethod, …).     */

/** CartError, e.g. RUM.cartError('SERVER_ERROR', { itemID, invtID, errorCode: RUM.errorCode(body) }). */
function rumCartError(errorType: string, data: Record<string, unknown> = {}): void {
	recordRumEvent(RUM_EVENT.CART_ERROR, Object.assign({}, data, { errorType: errorType }));
}

/** ProductError, e.g. RUM.productError('SELECT_OPTION', { itemID }). */
function rumProductError(errorType: string, data: Record<string, unknown> = {}): void {
	recordRumEvent(RUM_EVENT.PRODUCT_ERROR, Object.assign({}, data, { errorType: errorType }));
}

/** CheckoutError, e.g. RUM.checkoutError('SERVER_ERROR', { step: 'promo', errorCode }). */
function rumCheckoutError(errorType: string, data: Record<string, unknown> = {}): void {
	recordRumEvent(RUM_EVENT.CHECKOUT_ERROR, Object.assign({}, data, { errorType: errorType }));
}

/** CheckoutEvent milestone, e.g. RUM.checkoutEvent('ORDER_SUBMITTED', { paymentMethod }). */
function rumCheckoutEvent(action: string, data: Record<string, unknown> = {}): void {
	recordRumEvent(RUM_EVENT.CHECKOUT_EVENT, Object.assign({}, data, { action: action }));
}

/** Default processor-failure alert → allowlisted code + paymentMethod map (VERISIGN UI strings; extend per site). */
const RUM_PAYMENT_ALERT_MAP: { re: RegExp; code: string; method: string }[] = [
	{ re: /^declined/i,                          code: 'CC_DECLINED',         method: 'Credit Card' }, // 'Declined'
	{ re: /incorrect gift card/i,                code: 'GIFTCARD_INVALID',    method: 'Gift Card' },   // 'Incorrect Gift Card Information!'
	{ re: /not signed up for this tender type/i, code: 'TENDER_TYPE_INVALID', method: 'Gift Card' },   // 'Not signed up for this tender type' — gift-card tender not enrolled
	{ re: /credit card number is incorrect/i,    code: 'CC_NUMBER_INVALID',   method: 'Credit Card' }, // 'Credit Card Number is incorrect'
	{ re: /insufficient funds/i,                 code: 'CC_INSUFFICIENT_FUNDS', method: 'Credit Card' }, // 'Insufficient funds available'
	{ re: /invalid transaction type/i,           code: 'TXN_TYPE_INVALID',    method: 'Credit Card' }, // 'Invalid transaction type' (Payflow result 3)
	{ re: /cvv2 mismatch/i,                      code: 'CC_CVV_MISMATCH',     method: 'Credit Card' }, // 'CVV2 Mismatch'
	// Security-filter copy is shown both for gateway declines and for approved-then-voided orders, so
	// these codes can't say who rejected the card. The two wordings get separate codes to keep the paths apart.
	{ re: /triggered one or more credit card security filters/i,   code: 'CC_SECURITY_FILTER',        method: 'Credit Card' }, // 'Your transaction has triggered one or more credit card security filters. …'
	{ re: /triggered the following credit card security filters/i, code: 'CC_SECURITY_FILTER_DETAIL', method: 'Credit Card' }, // 'Your order has triggered the following credit card security filters below. …'
];

/**
 * Track server-side payment failures that surface as a plain alert(). The OrderCentral codebehind
 * injects a cookie-guarded alert('message') at the end of the post-back for declines, gift-card
 * errors, etc. (it does NOT call doUserErrorMessage). We wrap window.alert ONCE and emit a
 * CheckoutError per failure so nothing is silently dropped:
 *   - mapped messages   → errorType 'PAYMENT_FAILED', allowlisted errorCode (CC_DECLINED, …);
 *   - unmapped messages → errorType 'PAYMENT_ALERT', errorCode 'UNMAPPED', plus the curated alert
 *     text via rumSnippet (these are server-curated UI strings, known PII-free — NOT raw bodies).
 * Keeping unmapped in a separate bucket avoids mislabeling client-side validation prompts (Terms,
 * "enter a valid card", …) as real processor failures. Native alert always still fires (UX unchanged).
 * Idempotent (guards window.__rumAlertHooked); install BEFORE the end-of-form alert runs.
 */
function rumHookPaymentAlerts(extraMap?: { re: RegExp; code: string; method?: string }[]): void {
	try {
		const w = window as any;
		if (w.__rumAlertHooked) { return; }
		w.__rumAlertHooked = true;
		// Normalize site-supplied entries to the full shape (method defaults to '') so the concat is
		// type-safe and map[i].method is always a string, never undefined.
		const map = (extraMap && extraMap.length)
			? RUM_PAYMENT_ALERT_MAP.concat(extraMap.map(function (m) { return { re: m.re, code: m.code, method: m.method || '' }; }))
			: RUM_PAYMENT_ALERT_MAP;
		const nativeAlert = w.alert;
		w.alert = function (message: unknown) {
			try {
				const text = (message == null ? '' : String(message)).replace(/\s+/g, ' ').trim();
				let matched = '';
				let mappedMethod = '';
				for (let i = 0; i < map.length; i++) {
					if (map[i].re.test(text)) { matched = map[i].code; mappedMethod = map[i].method; break; }
				}
				// paymentMethod for parity with ORDER_SUBMITTED. The decline post-back resets the
				// card-type selects, so RUM.paymentMethod() is often '' here — fall back to the method
				// implied by the matched failure string.
				const paymentMethod = rumPaymentMethod() || mappedMethod;
				rumLog('alert intercepted →', matched || 'UNMAPPED', '· method:', paymentMethod || '(unknown)', '· raw:', text);
				if (matched) {
					rumCheckoutError('PAYMENT_FAILED', { step: 'payment', errorCode: matched, paymentMethod: paymentMethod });
				} else if (text) {
					rumCheckoutError('PAYMENT_ALERT', { step: 'payment', errorCode: 'UNMAPPED', paymentMethod: paymentMethod, message: rumSnippet(text) });
				}
			} catch (e) {
				rumLog('alert hook THREW (swallowed)', e);
			}
			return nativeAlert.apply(w, arguments);
		};
	} catch (e) {
		rumLog('hookPaymentAlerts THREW (swallowed)', e);
	}
}

/** Public surface: core primitives + semantic helpers + effective config. */
const RUM = {
	// config & taxonomy
	config: RUM_CONFIG,
	events: RUM_EVENT,
	// core primitives
	recordEvent: recordRumEvent,
	errorCode: rumErrorCode,
	snippet: rumSnippet,
	paymentMethod: rumPaymentMethod,
	markOrderInFlight: rumMarkOrderInFlight,
	// semantic helpers
	cartError: rumCartError,
	productError: rumProductError,
	checkoutError: rumCheckoutError,
	checkoutEvent: rumCheckoutEvent,
	hookPaymentAlerts: rumHookPaymentAlerts,
	// debug
	log: rumLog,
	/** Toggle debug logging at runtime. Can only enable on DEV (RUM_DEBUG bootstrap); disable always works. */
	setDebug: function (on: boolean): void { rumDebug = RUM_CONFIG.debug ? !!on : false; },
	get debug(): boolean { return rumDebug; },
};

// Exposed for other bundles and inline ASP includes. Built standalone into rum.min.js and loaded
// early on every page by the host's header.inc (`ImportJSWithHash("rum.min.js", true)`).
(window as any).RUM = RUM;
(window as any).recordRumEvent = recordRumEvent;
